import React, { useState, useEffect, useRef, useCallback } from "react"
import { router, usePage } from "@inertiajs/react"
import { Search, FileText, GitBranch, Users, Building2, Tag, Loader2, X, ChevronRight, ChevronDown, ArrowLeft } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useSearch } from "@/lib/contexts/search-context"

// Category icons mapping
const categoryIcons = {
  tasks: FileText,
  workflows: GitBranch,
  users: Users,
  departments: Building2,
  categories: Tag,
}

// Category labels
const categoryLabels = {
  tasks: "Tasks",
  workflows: "Workflows",
  users: "Users",
  departments: "Departments",
  categories: "Categories",
}

// Category routes for "See all"
const categoryRoutes = {
  tasks: "/tasks",
  workflows: "/workflows",
  users: "/users",
  departments: "/departments",
  categories: "/categories",
}

// Status badge colors
const statusColors = {
  // Task statuses
  "Active": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "Pending": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  "Completed": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "Missed": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  "To Do": "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  "In Progress": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  // Workflow statuses
  "active": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "inactive": "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  "draft": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
}


export function GlobalSearch({ onExpandChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMobileExpanded, setIsMobileExpanded] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState({
    tasks: { items: [], totalCount: 0 },
    workflows: { items: [], totalCount: 0 },
    users: { items: [], totalCount: 0 },
    departments: { items: [], totalCount: 0 },
    categories: { items: [], totalCount: 0 },
  })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  // Track which categories are expanded to show all results
  const [expandedCategories, setExpandedCategories] = useState(new Set())
  // Track loading state for individual category expansions
  const [loadingCategories, setLoadingCategories] = useState(new Set())
  
  const mobileInputRef = useRef(null)
  const desktopInputRef = useRef(null)
  const containerRef = useRef(null)
  const searchTimeoutRef = useRef(null)

  // Notify parent when mobile expanded state changes
  useEffect(() => {
    onExpandChange?.(isMobileExpanded)
  }, [isMobileExpanded, onExpandChange])

  // Handle mobile search icon click
  const handleMobileSearchClick = () => {
    setIsMobileExpanded(true)
    setIsOpen(true)
    setTimeout(() => mobileInputRef.current?.focus(), 100)
  }

  // Handle closing mobile expanded search
  const handleMobileClose = () => {
    setIsMobileExpanded(false)
    setIsOpen(false)
    setQuery("")
    setResults({
      tasks: { items: [], totalCount: 0 },
      workflows: { items: [], totalCount: 0 },
      users: { items: [], totalCount: 0 },
      departments: { items: [], totalCount: 0 },
      categories: { items: [], totalCount: 0 },
    })
  }

  // Get the search context to share query with pages
  const { updateSearchQuery, setIsSearchFocused } = useSearch()

  // Update the context whenever query changes
  useEffect(() => {
    updateSearchQuery(query)
  }, [query, updateSearchQuery])

  // Update focus state in context
  useEffect(() => {
    setIsSearchFocused(isOpen)
  }, [isOpen, setIsSearchFocused])

  // Flatten results for keyboard navigation (only include displayed items)
  const flattenedResults = useCallback(() => {
    const flat = []
    Object.entries(results).forEach(([category, data]) => {
      const allItems = data.items || []
      const isExpanded = expandedCategories.has(category)
      // Only include the items that are actually displayed
      const displayItems = isExpanded ? allItems : allItems.slice(0, 5)
      displayItems.forEach((item) => {
        flat.push({ ...item, category })
      })
    })
    return flat
  }, [results, expandedCategories])

  // Handle keyboard shortcut (Ctrl+K / Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        setIsOpen(true)
        
        // Check if on mobile and set mobile expanded
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
        if (isMobile) {
          setIsMobileExpanded(true)
          setTimeout(() => mobileInputRef.current?.focus(), 100)
        } else {
          setTimeout(() => desktopInputRef.current?.focus(), 50)
        }
      }
      
      // Close on Escape
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false)
        setIsMobileExpanded(false)
        desktopInputRef.current?.blur()
        mobileInputRef.current?.blur()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen])

  // Close dropdown when clicking outside (only for desktop)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't handle click outside when mobile expanded (has its own close button)
      if (isMobileExpanded) return
      
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isMobileExpanded])

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (query.length < 2) {
      setResults({
        tasks: { items: [], totalCount: 0 },
        workflows: { items: [], totalCount: 0 },
        users: { items: [], totalCount: 0 },
        departments: { items: [], totalCount: 0 },
        categories: { items: [], totalCount: 0 },
      })
      setExpandedCategories(new Set())
      setIsLoading(false)
      return
    }

    // Reset expanded categories when query changes
    setExpandedCategories(new Set())

    setIsLoading(true)
    
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch("/search", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
          },
          body: JSON.stringify({ query, limit: 5 }),
        })
        
        if (response.ok) {
          const data = await response.json()
          setResults(data)
        }
      } catch (error) {
        console.error("Search error:", error)
      } finally {
        setIsLoading(false)
      }
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [query])

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const items = flattenedResults()
    
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1))
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault()
      const selectedItem = items[selectedIndex]
      if (selectedItem) {
        handleItemClick(selectedItem, selectedItem.category)
      }
    }
  }

  // Handle item click - navigate to page and open modal for the selected item
  const handleItemClick = (item, category) => {
    setIsOpen(false)
    setIsMobileExpanded(false)

    switch (category) {
      case "tasks":
        router.visit(`/tasks?task=${item.id}`)
        break
      case "workflows":
        router.visit(`/workflows/${item.id}`)
        break
      case "users":
        router.visit(`/users?user=${item.id}`)
        break
      case "departments":
        router.visit(`/departments?department=${item.id}`)
        break
      case "categories":
        router.visit(`/categories?category=${item.id}`)
        break
      default:
        break
    }
  }

  // Handle "See all" click - load all results for this category in the dropdown
  const handleSeeAll = async (category) => {
    // If already expanded, collapse it
    if (expandedCategories.has(category)) {
      setExpandedCategories(prev => {
        const newSet = new Set(prev)
        newSet.delete(category)
        return newSet
      })
      return
    }

    // Set loading state for this category
    setLoadingCategories(prev => {
      const newSet = new Set(prev)
      newSet.add(category)
      return newSet
    })

    try {
      // Fetch all results for this category (limit: 0 means no limit)
      const response = await fetch("/search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({ query, limit: 0, category }),
      })
      
      if (response.ok) {
        const data = await response.json()
        // Update only the specific category with all its results
        setResults(prev => ({
          ...prev,
          [category]: data[category] || prev[category]
        }))
        // Mark this category as expanded
        setExpandedCategories(prev => {
          const newSet = new Set(prev)
          newSet.add(category)
          return newSet
        })
      }
    } catch (error) {
      console.error("Error fetching all results for category:", error)
    } finally {
      // Remove loading state
      setLoadingCategories(prev => {
        const newSet = new Set(prev)
        newSet.delete(category)
        return newSet
      })
    }
  }

  // Clear search
  const clearSearch = () => {
    setQuery("")
    setResults({
      tasks: { items: [], totalCount: 0 },
      workflows: { items: [], totalCount: 0 },
      users: { items: [], totalCount: 0 },
      departments: { items: [], totalCount: 0 },
      categories: { items: [], totalCount: 0 },
    })
    setSelectedIndex(-1)
    // Focus the visible input
    if (isMobileExpanded) {
      mobileInputRef.current?.focus()
    } else {
      desktopInputRef.current?.focus()
    }
  }

  // Check if there are any results
  const hasResults = Object.values(results).some((data) => (data.items?.length || 0) > 0)

  // Render a single result item
  const renderResultItem = (item, category, globalIndex) => {
    const Icon = categoryIcons[category]
    const isSelected = selectedIndex === globalIndex

    return (
      <button
        key={`${category}-${item.id}`}
        className={cn(
          "w-full flex items-start gap-3 px-3 py-2 text-left rounded-md transition-colors",
          "hover:bg-accent focus:bg-accent focus:outline-none",
          isSelected && "bg-accent"
        )}
        onClick={() => handleItemClick(item, category)}
        onMouseEnter={() => setSelectedIndex(globalIndex)}
      >
        <Icon className="h-4 w-4 mt-1 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">
              {category === "tasks" ? `#${item.id} - ${item.name}` : item.name}
            </span>
            {item.status && (
              <Badge 
                variant="secondary" 
                className={cn("text-xs shrink-0", statusColors[item.status])}
              >
                {item.status}
              </Badge>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {item.description}
            </p>
          )}
          {category === "tasks" && item.users?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Assigned to: {item.users.map((u) => u.name).join(", ")}
            </p>
          )}
          {category === "tasks" && item.matchType === 'category' && item.matchValue && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
              Match in category: "{item.matchValue}"
            </p>
          )}
          {category === "tasks" && item.matchType === 'assignee' && item.matchValue && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
              Match in assignee: "{item.matchValue}"
            </p>
          )}
          {category === "workflows" && item.matchedStep && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
              {item.matchedStep.matchType === 'category' 
                ? `Match in ${item.matchedStep.type} node category: "${item.matchedStep.category}"`
                : `Match in ${item.matchedStep.type} node: "${item.matchedStep.name}"`
              }
            </p>
          )}
          {category === "users" && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {item.email}{item.department && ` - ${item.department}`}
            </p>
          )}
          {category === "departments" && item.parent && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Parent: {item.parent}
            </p>
          )}
        </div>
      </button>
    )
  }

  // Calculate global index for keyboard navigation
  let globalIndex = -1

  return (
    <>
      {/* Mobile Search Icon Button - visible only on small screens when not expanded */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "md:hidden h-9 w-9",
          isMobileExpanded && "hidden"
        )}
        onClick={handleMobileSearchClick}
        aria-label="Search"
      >
        <Search className="h-5 w-5" />
      </Button>

      {/* Mobile Expanded Search Overlay */}
      {isMobileExpanded && (
        <div className="md:hidden fixed inset-0 z-50 bg-background">
          <div className="flex items-center h-16 px-4 border-b gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={handleMobileClose}
              aria-label="Close search"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={mobileInputRef}
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-9 pr-8 h-9 w-full"
                autoComplete="off"
              />
              {query && (
                <button
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          
          {/* Mobile Results */}
          <div className="flex-1 overflow-y-auto max-h-[calc(100vh-4rem)]">
            {query.length >= 2 ? (
              isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : hasResults ? (
                <div className="py-2">
                  {Object.entries(results).map(([category, data]) => {
                    const allItems = data.items || []
                    const totalCount = data.totalCount || 0
                    const isExpanded = expandedCategories.has(category)
                    const isLoadingCategory = loadingCategories.has(category)
                    // Show only first 5 items when not expanded
                    const displayItems = isExpanded ? allItems : allItems.slice(0, 5)
                    
                    if (allItems.length === 0) return null
                    
                    return (
                      <div key={category} className="mb-1 last:mb-0">
                        <div className="flex items-center justify-between px-4 py-1.5 bg-popover border-b sticky top-0 z-10">
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                            {React.createElement(categoryIcons[category], { className: "h-3 w-3" })}
                            {categoryLabels[category]}
                            <span className="font-normal">
                              ({displayItems.length}{isExpanded ? '' : ` of ${totalCount}`})
                            </span>
                          </h3>
                          {(totalCount > 5 || isExpanded) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[10px]"
                              onClick={() => handleSeeAll(category)}
                              disabled={isLoadingCategory}
                            >
                              {isLoadingCategory ? (
                                <>
                                  <Loader2 className="h-3 w-3 mr-0.5 animate-spin" />
                                  Loading...
                                </>
                              ) : isExpanded ? (
                                <>
                                  Show less
                                  <ChevronDown className="h-3 w-3 ml-0.5 rotate-180" />
                                </>
                              ) : (
                                <>
                                  See all
                                  <ChevronRight className="h-3 w-3 ml-0.5" />
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        <div className={cn(
                          "px-2 py-1",
                          isExpanded && "max-h-[300px] overflow-y-auto"
                        )}>
                          {displayItems.map((item) => {
                            globalIndex++
                            return renderResultItem(item, category, globalIndex)
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">No results found for "{query}"</p>
                </div>
              )
            ) : query.length > 0 ? (
              <div className="py-6 text-center text-muted-foreground">
                <p className="text-sm">Type at least 2 characters to search</p>
              </div>
            ) : (
              <div className="py-6 text-center text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-20" />
                <p className="text-sm">Search tasks, workflows, users...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop Search - hidden on mobile */}
      <div 
        ref={containerRef} 
        className={cn(
          "relative transition-all duration-200 ease-out hidden md:block",
          isOpen ? "w-[500px]" : "w-64"
        )}
      >
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={desktopInputRef}
            type="text"
            placeholder="Search (Ctrl+K)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            className="pl-9 pr-8 h-9 w-full"
            autoComplete="off"
          />
          {query && (
            <button
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Dropdown Results */}
        {isOpen && (
          <div className="absolute top-full right-0 mt-1 bg-popover border rounded-lg shadow-lg z-50 w-[500px] flex flex-col max-h-[70vh]">
          <div className="flex-1 overflow-y-auto">
            {query.length >= 2 ? (
              isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : hasResults ? (
                <div className="py-2">
                  {Object.entries(results).map(([category, data]) => {
                    const allItems = data.items || []
                    const totalCount = data.totalCount || 0
                    const isExpanded = expandedCategories.has(category)
                    const isLoadingCategory = loadingCategories.has(category)
                    // Show only first 5 items when not expanded
                    const displayItems = isExpanded ? allItems : allItems.slice(0, 5)
                    
                    if (allItems.length === 0) return null
                    
                    return (
                      <div key={category} className="mb-1 last:mb-0">
                        {/* Category Header */}
                        <div className="flex items-center justify-between px-3 py-1.5 bg-popover border-b sticky top-0 z-10">
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                            {React.createElement(categoryIcons[category], { className: "h-3 w-3" })}
                            {categoryLabels[category]}
                            <span className="font-normal">
                              ({displayItems.length}{isExpanded ? '' : ` of ${totalCount}`})
                            </span>
                          </h3>
                          {(totalCount > 5 || isExpanded) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[10px]"
                              onClick={() => handleSeeAll(category)}
                              disabled={isLoadingCategory}
                            >
                              {isLoadingCategory ? (
                                <>
                                  <Loader2 className="h-3 w-3 mr-0.5 animate-spin" />
                                  Loading...
                                </>
                              ) : isExpanded ? (
                                <>
                                  Show less
                                  <ChevronDown className="h-3 w-3 ml-0.5 rotate-180" />
                                </>
                              ) : (
                                <>
                                  See all
                                  <ChevronRight className="h-3 w-3 ml-0.5" />
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        
                        {/* Category Items */}
                        <div className={cn(
                          "px-1 py-1",
                          isExpanded && "max-h-[300px] overflow-y-auto"
                        )}>
                          {displayItems.map((item) => {
                            globalIndex++
                            return renderResultItem(item, category, globalIndex)
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">No results found for "{query}"</p>
                </div>
              )
            ) : query.length > 0 ? (
              <div className="py-6 text-center text-muted-foreground">
                <p className="text-sm">Type at least 2 characters to search</p>
              </div>
            ) : (
              <div className="py-6 text-center text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-20" />
                <p className="text-sm">Search tasks, workflows, users...</p>
              </div>
            )}
          </div>
          
            {/* Footer hint */}
            <div className="border-t px-3 py-1.5 bg-muted/30 shrink-0">
              <p className="text-[10px] text-muted-foreground">
                <kbd className="px-1 py-0.5 bg-muted rounded text-[9px] font-mono">↑↓</kbd> navigate
                <span className="mx-1.5">·</span>
                <kbd className="px-1 py-0.5 bg-muted rounded text-[9px] font-mono">Enter</kbd> select
                <span className="mx-1.5">·</span>
                <kbd className="px-1 py-0.5 bg-muted rounded text-[9px] font-mono">Esc</kbd> close
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
